// src/core/services/auth-service/security.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, interval, Subscription } from 'rxjs';
import { Router } from '@angular/router';

interface SecurityViolation {
  type: 'URL_MANIPULATION' | 'TOKEN_MISMATCH' | 'INVALID_TOKEN_FORMAT' | 'STEP_VIOLATION';
  message: string;
  timestamp: Date;
}

interface TokenSecurityCheck {
  isValid: boolean;
  reason?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SecurityService {
  private securityViolations$ = new BehaviorSubject<SecurityViolation[]>([]);
  private monitoringSubscription?: Subscription;
  private isSecurityEnabled = true;
  private currentStep = 1;
  private validatedToken: string | null = null;
  private urlMonitoringActive = false;

  constructor(private router: Router) {
    this.initializeUrlMonitoring();
  }

  // Get security violations observable
  getSecurityViolations(): Observable<SecurityViolation[]> {
    return this.securityViolations$.asObservable();
  }

  // Update current registration step
  updateRegistrationStep(step: number): void {
    this.currentStep = step;
    
    if (step === 3) {
      this.startTokenMonitoring();
    } else {
      this.stopTokenMonitoring();
    }
  }

  // Set validated token for monitoring
  setValidatedToken(token: string): void {
    this.validatedToken = token;
  }

  // Validate token security
  validateTokenSecurity(token: string): TokenSecurityCheck {
    if (!this.isSecurityEnabled) {
      return { isValid: true };
    }

    // Check token format (JWT should have 3 parts)
    if (!this.isValidJwtFormat(token)) {
      return {
        isValid: false,
        reason: 'Invalid JWT format'
      };
    }

    // Check token length (reasonable bounds)
    if (token.length < 100 || token.length > 2000) {
      return {
        isValid: false,
        reason: 'Token length out of bounds'
      };
    }

    // Check for obvious tampering patterns
    if (this.detectTamperingPatterns(token)) {
      return {
        isValid: false,
        reason: 'Token tampering patterns detected'
      };
    }

    return { isValid: true };
  }

  // Temporarily disable security (for navigation)
  temporarilyDisableSecurity(durationMs: number): void {
    this.isSecurityEnabled = false;
    
    // Also clear any existing violations during the disable period
    this.securityViolations$.next([]);
    
    setTimeout(() => {
      this.isSecurityEnabled = true;
    }, durationMs);
  }

  // Clear all security data
  clearSecurityData(): void {
    this.stopTokenMonitoring();
    this.validatedToken = null;
    this.currentStep = 1;
    this.securityViolations$.next([]);
    this.isSecurityEnabled = true;
  }

  // Destroy service
  destroy(): void {
    this.stopTokenMonitoring();
    if (this.monitoringSubscription) {
      this.monitoringSubscription.unsubscribe();
    }
  }

  // Private methods
  private initializeUrlMonitoring(): void {
    // Monitor URL changes for suspicious activity
    this.router.events.subscribe(() => {
      if (this.urlMonitoringActive && this.currentStep === 3) {
        this.checkUrlIntegrity();
      }
    });
  }

  private startTokenMonitoring(): void {
    if (this.monitoringSubscription) {
      this.monitoringSubscription.unsubscribe();
    }

    this.urlMonitoringActive = true;

    // Only start monitoring after a short delay to allow navigation to complete
    setTimeout(() => {
      // Check every 3 seconds (increased from 2 seconds to reduce false positives)
      this.monitoringSubscription = interval(3000).subscribe(() => {
        if (this.isSecurityEnabled && this.currentStep === 3 && this.validatedToken) {
          this.performSecurityCheck();
        }
      });
    }, 2000); // Wait 2 seconds before starting monitoring
  }

  private stopTokenMonitoring(): void {
    this.urlMonitoringActive = false;
    if (this.monitoringSubscription) {
      this.monitoringSubscription.unsubscribe();
    }
  }

  private performSecurityCheck(): void {
    // Only perform checks if we're in step 3 and have a validated token
    if (this.currentStep !== 3 || !this.validatedToken) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const currentToken = urlParams.get('token');

    // Check if token is missing when it should be present
    if (!currentToken) {
      this.reportViolation({
        type: 'URL_MANIPULATION',
        message: 'Token removed from URL during sensitive operation',
        timestamp: new Date()
      });
      return;
    }

    // Check if token matches our validated token
    if (currentToken !== this.validatedToken) {
      this.reportViolation({
        type: 'TOKEN_MISMATCH',
        message: 'Token in URL does not match validated token',
        timestamp: new Date()
      });
      return;
    }

    // Validate token format
    const securityCheck = this.validateTokenSecurity(currentToken);
    if (!securityCheck.isValid) {
      this.reportViolation({
        type: 'INVALID_TOKEN_FORMAT',
        message: securityCheck.reason || 'Token security validation failed',
        timestamp: new Date()
      });
      return;
    }

  }

  private checkUrlIntegrity(): void {
    // Additional URL integrity checks can be added here
    const currentUrl = window.location.href;
    
    // Check for suspicious URL patterns
    if (this.detectSuspiciousUrlPatterns(currentUrl)) {
      this.reportViolation({
        type: 'URL_MANIPULATION',
        message: 'Suspicious URL manipulation detected',
        timestamp: new Date()
      });
    }
  }

  private isValidJwtFormat(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }

      // Try to decode header and payload
      JSON.parse(atob(parts[0]));
      JSON.parse(atob(parts[1]));
      
      return true;
    } catch (error) {
      return false;
    }
  }

  private detectTamperingPatterns(token: string): boolean {
    // Check for common tampering patterns
    const suspiciousPatterns = [
      /\.{4,}/, // Multiple consecutive dots
      /[^A-Za-z0-9+/=._-]/, // Invalid characters for JWT
      /^[.]+/, // Starting with dots
      /[.]+$/, // Ending with dots
    ];

    return suspiciousPatterns.some(pattern => pattern.test(token));
  }

  private detectSuspiciousUrlPatterns(url: string): boolean {
    // Check for suspicious URL patterns
    const suspiciousPatterns = [
      /javascript:/i,
      /data:/i,
      /vbscript:/i,
      /<script/i,
      /onload=/i,
      /onerror=/i
    ];

    return suspiciousPatterns.some(pattern => pattern.test(url));
  }

  private reportViolation(violation: SecurityViolation): void {
    
    const current = this.securityViolations$.value;
    const updated = [...current, violation];
    
    // Keep only last 10 violations
    if (updated.length > 10) {
      updated.splice(0, updated.length - 10);
    }
    
    this.securityViolations$.next(updated);
  }
}